'use client';

import React, { useState, useRef, useCallback } from 'react';
import {
  Folder,
  File,
  Upload,
  Download,
  Trash2,
  Plus,
  Search,
  MoreVertical,
  FolderPlus,
  FileText,
  Image as ImageIcon,
  Archive,
  Video,
  Music,
  Code,
  Grid,
  List,
  ChevronRight,
  ChevronDown,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useBusinessFiles } from '@/hooks/react-query/files/use-business-files';
import { formatBytes } from '@/lib/utils/file-utils';

interface FileItem {
  id: string;
  name: string;
  type: 'file' | 'folder';
  size?: number;
  modified: Date;
  parent_folder?: string;
  business_id: string;
  file_extension?: string;
  storage_path?: string;
}

interface FileManagerProps {
  businessId: string;
  className?: string;
}

const getFileIcon = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  
  switch (extension) {
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'svg':
    case 'webp':
      return <ImageIcon className="h-4 w-4 text-blue-500" />;
    case 'mp4':
    case 'avi':
    case 'mov':
    case 'webm':
      return <Video className="h-4 w-4 text-purple-500" />;
    case 'mp3':
    case 'wav':
    case 'flac':
    case 'aac':
      return <Music className="h-4 w-4 text-green-500" />;
    case 'zip':
    case 'rar':
    case '7z':
    case 'tar':
      return <Archive className="h-4 w-4 text-orange-500" />;
    case 'js':
    case 'ts':
    case 'jsx':
    case 'tsx':
    case 'py':
    case 'java':
    case 'cpp':
    case 'html':
    case 'css':
      return <Code className="h-4 w-4 text-red-500" />;
    case 'txt':
    case 'md':
    case 'doc':
    case 'docx':
    case 'pdf':
      return <FileText className="h-4 w-4 text-gray-500" />;
    default:
      return <File className="h-4 w-4 text-gray-400" />;
  }
};

export function FileManager({ businessId, className }: FileManagerProps) {
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const {
    data: files,
    isLoading,
    createFolder,
    uploadFiles,
    deleteFiles,
    downloadFile,
    isCreatingFolder,
    isUploading,
    isDeleting,
  } = useBusinessFiles(businessId);

  const currentFolderFiles = files?.filter(file => file.parent_folder === currentFolder) || [];
  const filteredFiles = currentFolderFiles.filter(file =>
    file.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const breadcrumbs = React.useMemo(() => {
    if (!currentFolder) return [];
    
    const crumbs: Array<{ id: string; name: string }> = [];
    let folder = files?.find(f => f.id === currentFolder);
    
    while (folder) {
      crumbs.unshift({ id: folder.id, name: folder.name });
      folder = files?.find(f => f.id === folder.parent_folder);
    }
    
    return crumbs;
  }, [currentFolder, files]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleFileUpload = useCallback(async (uploadedFiles: File[]) => {
    try {
      await uploadFiles.mutateAsync({
        files: uploadedFiles,
        parentFolder: currentFolder,
      });
      toast.success(`Successfully uploaded ${uploadedFiles.length} file(s)`);
    } catch (error) {
      toast.error('Failed to upload files');
      console.error('Upload error:', error);
    }
  }, [uploadFiles, currentFolder]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      handleFileUpload(droppedFiles);
    }
  }, [handleFileUpload]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const uploadedFiles = Array.from(e.target.files);
      handleFileUpload(uploadedFiles);
      e.target.value = ''; // Reset input
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Folder name is required');
      return;
    }

    try {
      await createFolder.mutateAsync({
        name: newFolderName,
        parentFolder: currentFolder,
      });
      toast.success('Folder created successfully');
      setNewFolderName('');
      setShowNewFolderDialog(false);
    } catch (error) {
      toast.error('Failed to create folder');
      console.error('Create folder error:', error);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedFiles.size === 0) {
      toast.error('No files selected');
      return;
    }

    try {
      await deleteFiles.mutateAsync(Array.from(selectedFiles));
      toast.success(`Successfully deleted ${selectedFiles.size} item(s)`);
      setSelectedFiles(new Set());
    } catch (error) {
      toast.error('Failed to delete files');
      console.error('Delete error:', error);
    }
  };

  const handleFileClick = async (file: FileItem) => {
    if (file.type === 'folder') {
      setCurrentFolder(file.id);
      setSelectedFiles(new Set());
    } else {
      // Handle file download
      await handleDownloadFile(file.id, file.name);
    }
  };

  const handleDownloadFile = async (fileId: string, fileName: string) => {
    try {
      setDownloadingFiles(prev => new Set(prev).add(fileId));
      const signedUrl = await downloadFile(fileId);
      
      // Open the signed URL in a new tab to trigger download
      window.open(signedUrl, '_blank');
      
      toast.success(`Downloading ${fileName}`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Failed to download ${fileName}`);
    } finally {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
    }
  };

  const handleFileSelect = (fileId: string, isSelected: boolean) => {
    const newSelected = new Set(selectedFiles);
    if (isSelected) {
      newSelected.add(fileId);
    } else {
      newSelected.delete(fileId);
    }
    setSelectedFiles(newSelected);
  };

  if (isLoading) {
    return (
      <div className={cn("p-4 space-y-4", className)}>
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-8 w-24" />
        </div>
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div 
      className={cn("flex flex-col h-full bg-white dark:bg-gray-900", className)}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">
            Business Files
          </h3>
          {selectedFiles.size > 0 && (
            <Badge variant="secondary" className="text-xs">
              {selectedFiles.size} selected
            </Badge>
          )}
        </div>
        
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
          >
            {viewMode === 'grid' ? <List className="h-4 w-4" /> : <Grid className="h-4 w-4" />}
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                <Upload className="mr-2 h-4 w-4" />
                Upload Files
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowNewFolderDialog(true)}>
                <FolderPlus className="mr-2 h-4 w-4" />
                New Folder
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <div className="flex items-center px-4 py-2 border-b border-gray-100 dark:border-gray-800">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentFolder(null)}
            className="p-1 h-6 text-xs"
          >
            Root
          </Button>
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.id}>
              <ChevronRight className="h-3 w-3 text-gray-400 mx-1" />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentFolder(crumb.id)}
                className="p-1 h-6 text-xs"
              >
                {crumb.name}
              </Button>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="p-4 border-b border-gray-100 dark:border-gray-800">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search files and folders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-8 text-sm"
          />
        </div>
      </div>

      {/* Action Bar */}
      {selectedFiles.size > 0 && (
        <div className="flex items-center justify-between px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800">
          <span className="text-sm text-blue-700 dark:text-blue-300">
            {selectedFiles.size} item(s) selected
          </span>
          <div className="flex space-x-2">
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDeleteSelected}
              disabled={isDeleting}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedFiles(new Set())}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* File List */}
      <div className="flex-1 overflow-auto">
        {dragActive && (
          <div className="absolute inset-0 bg-blue-50 dark:bg-blue-900/20 border-2 border-dashed border-blue-300 dark:border-blue-600 rounded-lg flex items-center justify-center z-10">
            <div className="text-center">
              <Upload className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <p className="text-blue-700 dark:text-blue-300">Drop files here to upload</p>
            </div>
          </div>
        )}

        {filteredFiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Folder className="h-12 w-12 mb-4" />
            <p className="text-sm">
              {searchQuery ? 'No files match your search' : 'This folder is empty'}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-2"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload your first file
            </Button>
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'grid grid-cols-4 gap-4 p-4' : 'space-y-1'}>
            {filteredFiles.map((file) => (
              <div
                key={file.id}
                className={cn(
                  "group relative",
                  viewMode === 'grid'
                    ? "p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                    : "flex items-center justify-between p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                )}
              >
                <div
                  className="flex items-center flex-1 min-w-0"
                  onClick={() => handleFileClick(file)}
                >
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedFiles.has(file.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleFileSelect(file.id, e.target.checked);
                      }}
                      className="mr-2"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {file.type === 'folder' ? (
                      <Folder className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    ) : (
                      getFileIcon(file.name)
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {file.name}
                      </p>
                      {viewMode === 'list' && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {file.type === 'file' && file.size && formatBytes(file.size)} • {' '}
                          {file.modified.toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {file.type === 'file' && (
                      <>
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadFile(file.id, file.name);
                          }}
                          disabled={downloadingFiles.has(file.id)}
                        >
                          <Download className="mr-2 h-4 w-4" />
                          {downloadingFiles.has(file.id) ? 'Downloading...' : 'Download'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFileSelect(file.id, true);
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* New Folder Dialog */}
      <Dialog open={showNewFolderDialog} onOpenChange={setShowNewFolderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogDescription>
              Enter a name for your new folder.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="folderName">Folder Name</Label>
              <Input
                id="folderName"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Enter folder name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateFolder();
                  }
                }}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowNewFolderDialog(false);
                  setNewFolderName('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateFolder}
                disabled={isCreatingFolder || !newFolderName.trim()}
              >
                {isCreatingFolder ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
